// Ferie V1 — motore di maturazione ferie
// Funzioni pure e testabili. Nessun side-effect.

/**
 * Ritorna il numero di giorni del mese (month è 1-based).
 */
export function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

/**
 * ISO date "YYYY-MM-DD" -> Date UTC (mezzogiorno per evitare DST issues)
 */
export function parseDate(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function toIso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Frazione (0..1) del mese [year, month] che è compresa nell'intervallo [start, end] inclusi.
 * start/end sono Date. month è 1-based.
 */
export function monthFractionInRange(year, month, start, end) {
  const first = new Date(year, month - 1, 1);
  const last  = new Date(year, month - 1, daysInMonth(year, month));
  const a = start > first ? start : first;
  const b = end   < last  ? end   : last;
  if (a > b) return 0;
  const totalDays = daysInMonth(year, month);
  const workedDays = Math.round((b - a) / 86400000) + 1;
  return workedDays / totalDays;
}

/**
 * Calcola i giorni maturati in `year` a partire dalla config del dipendente,
 * considerando data inizio, data fine rapporto e asOfDate.
 *
 * config: {
 *   dataInizio: 'YYYY-MM-DD',
 *   dataFine:   'YYYY-MM-DD' | null,
 *   ferieAnnue: number,        // 30, 36, ecc.
 *   metodoMaturazione: 'mensile'
 * }
 *
 * Ritorna number con 2 decimali.
 */
export function computeAccrued(config, year, asOfDate = new Date()) {
  const perMese = (config.ferieAnnue || 0) / 12;
  const jan1 = new Date(year, 0, 1);
  const dec31 = new Date(year, 11, 31);
  const start = parseDate(config.dataInizio);
  const endHire = parseDate(config.dataFine);
  if (!start || start > dec31) return 0;

  // limite superiore = min(dec31, asOfDate, dataFine)
  let upper = dec31;
  if (asOfDate < upper) upper = asOfDate;
  if (endHire && endHire < upper) upper = endHire;

  let lower = start > jan1 ? start : jan1;
  if (lower > upper) return 0;

  let totalFraction = 0;
  for (let m = 1; m <= 12; m++) {
    totalFraction += monthFractionInRange(year, m, lower, upper);
  }
  return Math.round(totalFraction * perMese * 100) / 100;
}

/**
 * Calcola giorni tra due date incluse.
 */
export function giorniTraDate(dalIso, alIso) {
  const dal = parseDate(dalIso);
  const al  = parseDate(alIso);
  if (!dal || !al || al < dal) return 0;
  return Math.round((al - dal) / 86400000) + 1;
}

export function giorniPolicyTraDate(dalIso, alIso, policy = {}) {
  const dal = parseDate(dalIso);
  const al = parseDate(alIso);
  if (!dal || !al || al < dal) return 0;
  const rule = policy.regolaMaturazione || {};
  const holidays = new Set(policy.giorniFestivi || rule.giorniFestivi || []);
  const criterion = policy.criterioConteggio || 'CALENDARIO';
  const configuredDays = policy.giorniLavorativi
    || rule.giorniLavorativi
    || policy.giorniSettimana
    || rule.giorniSettimana;
  const workDays = Number(policy.settimanaLavorativa)
    || (criterion.endsWith('_GIORNI') ? Number(criterion.split('_')[0]) : 0);
  let count = 0;
  for (let cursor = new Date(dal); cursor <= al; cursor.setDate(cursor.getDate() + 1)) {
    const iso = toIso(cursor);
    if (holidays.has(iso)) continue;
    const mondayIndex = (cursor.getDay() + 6) % 7;
    if (
      criterion === 'CALENDARIO'
      || (!['5_GIORNI', '6_GIORNI', 'PERSONALIZZATO'].includes(criterion))
      || (['5_GIORNI', '6_GIORNI'].includes(criterion) && mondayIndex < workDays)
      || (criterion === 'PERSONALIZZATO' && configuredDays && configuredDays.map(Number).includes(mondayIndex))
      || (criterion === 'PERSONALIZZATO' && !configuredDays)
    ) {
      count += 1;
    }
  }
  return count;
}

/**
 * Giorni inclusivi di un periodo che ricadono nell'anno/mese richiesto.
 * `after` esclude la parte già consolidata in una baseline.
 */
export function giorniSovrapposti(periodo, year, month = null, after = null, policy = null) {
  let start = parseDate(periodo.dal);
  let end = parseDate(periodo.al);
  if (!start || !end || end < start) return 0;
  const yearStart = new Date(year, 0, 1);
  const yearEnd = new Date(year, 11, 31);
  start = start > yearStart ? start : yearStart;
  end = end < yearEnd ? end : yearEnd;
  if (month) {
    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month - 1, daysInMonth(year, month));
    start = start > monthStart ? start : monthStart;
    end = end < monthEnd ? end : monthEnd;
  }
  if (after) {
    const afterDate = after instanceof Date ? after : parseDate(after);
    if (afterDate) start = start > afterDate ? start : new Date(afterDate.getFullYear(), afterDate.getMonth(), afterDate.getDate() + 1);
  }
  return start > end ? 0 : giorniPolicyTraDate(toIso(start), toIso(end), policy || {});
}

/**
 * Data una lista di periodi ferie del dipendente e l'anno di competenza,
 * ritorna aggregazioni per stato.
 *
 * periodo: { stato, giorni, annoCompetenza }
 */
export function aggregaPeriodi(periodi, dipendenteId, anno, policy = null) {
  const mine = periodi.filter(p => p.dipendenteId === dipendenteId && p.annoCompetenza === anno);
  const somma = (stati) => mine
    .filter(p => stati.includes(p.stato))
    .reduce((sum, periodo) => sum + giorniSovrapposti(periodo, anno, null, null, policy), 0);
  return {
    richieste:   somma(['richiesta']),
    approvate:   somma(['approvata']),      // approvata futura ma non ancora programmata
    programmate: somma(['programmata']),
    godute:      somma(['goduta']),
    rifiutate:   somma(['rifiutata']),
    annullate:   somma(['annullata']),
  };
}

/**
 * Calcolo saldo per un dipendente in un dato anno.
 *
 * config: come sopra + { rettifiche: number, annoCompetenza: number }
 * storico: array di { anno, stato: 'chiuso'|'aperto', residuo: number, godute: number, maturate: number }
 * periodi: array periodi ferie
 *
 * Ritorna:
 * {
 *   annoCompetenza, maturate, godute, programmate, richieste,
 *   riportoPrecedente, rettifiche, disponibile, totaleAnnuo,
 *   mesePerMese: [{mese, maturate, godute, disponibile}]
 * }
 */
export function calcolaSaldo({ config, storico = [], periodi = [], dipendenteId, asOfDate = new Date() }) {
  const anno = config.annoCompetenza;
  const rettifiche = config.rettifiche || 0;
  const annoPrec = anno - 1;
  const rec = storico.find(s => s.anno === annoPrec);
  const riportoPrecedente = rec ? (rec.residuo || 0) : 0;

  // BASELINE CONSOLIDATA (autorizzata): se presente e asOfDate >= baseline.data,
  // il saldo parte da baseline.saldo + maturato dopo la baseline + rettifiche - godute SUCCESSIVE.
  const hasBaseline = !!(config.baseline && config.baseline.data && typeof config.baseline.saldo === 'number');
  const baseDate = hasBaseline ? parseDate(config.baseline.data) : null;
  const useBaseline = hasBaseline && asOfDate >= baseDate;

  let maturate, godute, programmate, richieste;
  const periodiAnno = periodi.filter(p => p.dipendenteId === dipendenteId && p.annoCompetenza === anno);
  const policy = config.policy || config;
  const periodiDopoBaseline = (periodo) => giorniSovrapposti(
    periodo, anno, null, useBaseline ? baseDate : null, policy,
  );
  const sommaPeriodi = (stati) => periodiAnno
    .filter(p => stati.includes(p.stato))
    .reduce((sum, periodo) => sum + periodiDopoBaseline(periodo), 0);
  if (useBaseline) {
    // maturato dal giorno SUCCESSIVO alla baseline (baseline consolidata inclusiva)
    const dopo = new Date(baseDate); dopo.setDate(dopo.getDate() + 1);
    const dopoIso = toIso(dopo);
    maturate = computeAccrued({ ...config, dataInizio: dopoIso }, anno, asOfDate);
    godute = sommaPeriodi(['goduta']);
    programmate = sommaPeriodi(['programmata','approvata']);
    richieste = sommaPeriodi(['richiesta']);
  } else {
    maturate = computeAccrued(config, anno, asOfDate);
    godute = sommaPeriodi(['goduta']);
    programmate = sommaPeriodi(['programmata','approvata']);
    richieste = sommaPeriodi(['richiesta']);
  }

  const saldoInizialeLordo = useBaseline ? config.baseline.saldo : riportoPrecedente;
  const saldoIniziale = Math.round((saldoInizialeLordo + rettifiche) * 100) / 100;
  const disponibile = Math.round((saldoIniziale + maturate - godute - programmate) * 100) / 100;

  const mesePerMese = [];
  for (let m = 1; m <= 12; m++) {
    const fineMese = new Date(anno, m - 1, daysInMonth(anno, m));
    const cutoff = fineMese < asOfDate ? fineMese : asOfDate;
    const accrualConfig = useBaseline
      ? { ...config, dataInizio: toIso(new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate() + 1)) }
      : config;
    const monthUsed = periodiAnno
      .filter(p => p.stato === 'goduta')
      .reduce((sum, periodo) => sum + giorniSovrapposti(
        periodo, anno, m, useBaseline ? baseDate : null, policy,
      ), 0);
    const cumulativeUsed = periodiAnno
      .filter(p => p.stato === 'goduta')
      .reduce((sum, periodo) => sum + Array.from({ length: m }, (_, index) => (
        giorniSovrapposti(periodo, anno, index + 1, useBaseline ? baseDate : null, policy)
      )).reduce((periodSum, monthDays) => periodSum + monthDays, 0), 0);
    const cumulativePlanned = periodiAnno
      .filter(p => ['programmata', 'approvata'].includes(p.stato))
      .reduce((sum, periodo) => sum + Array.from({ length: m }, (_, index) => (
        giorniSovrapposti(periodo, anno, index + 1, useBaseline ? baseDate : null, policy)
      )).reduce((periodSum, monthDays) => periodSum + monthDays, 0), 0);
    const monthAccrued = computeAccrued(accrualConfig, anno, cutoff);
    mesePerMese.push({
      mese: m,
      maturate: monthAccrued,
      godute: monthUsed,
      programmate: periodiAnno
        .filter(p => ['programmata', 'approvata'].includes(p.stato))
        .reduce((sum, periodo) => sum + giorniSovrapposti(
          periodo, anno, m, useBaseline ? baseDate : null, policy,
        ), 0),
      disponibile: Math.round((
        saldoIniziale + monthAccrued - cumulativeUsed - cumulativePlanned
      ) * 100) / 100,
    });
  }

  return {
    annoCompetenza: anno,
    ferieAnnue: config.ferieAnnue,
    maturate,
    godute,
    programmate,
    richieste,
    riportoPrecedente,
    rettifiche,
    disponibile,
    mesePerMese,
    baselineUsata: useBaseline ? { data: config.baseline.data, saldo: config.baseline.saldo } : null,
  };
}

/**
 * Warning-first: verifica una nuova richiesta e ritorna lista avvisi (non blocca).
 */
export function verificaAvvisi({ config, saldo, dalIso, alIso, periodiEsistenti = [], dipendenteId, minimoCopertura = 0 }) {
  const avvisi = [];
  const giorni = giorniPolicyTraDate(dalIso, alIso, config.policy || config);

  if (!dalIso || !alIso) avvisi.push({ tipo: 'error', msg: 'Date mancanti' });
  if (giorni <= 0) avvisi.push({ tipo: 'error', msg: 'Intervallo date non valido' });

  const saldoDopo = Math.round((saldo.disponibile - giorni) * 100) / 100;
  if (saldoDopo < 0) {
    avvisi.push({ tipo: 'warning', msg: `Saldo insufficiente: dopo la richiesta risulterebbero ${saldoDopo} giorni.` });
  }

  const dal = parseDate(dalIso);
  const al  = parseDate(alIso);
  const annoDal = dal?.getFullYear();
  const annoAl  = al?.getFullYear();
  if (annoDal !== config.annoCompetenza || annoAl !== config.annoCompetenza) {
    avvisi.push({ tipo: 'warning', msg: `Le date sono fuori dall'anno di competenza (${config.annoCompetenza}).` });
  }

  // sovrapposizione con altre richieste dello stesso dipendente
  const overlap = periodiEsistenti.some(p =>
    p.dipendenteId === dipendenteId &&
    !['rifiutata','annullata'].includes(p.stato) &&
    !(parseDate(p.al) < dal || parseDate(p.dal) > al)
  );
  if (overlap) avvisi.push({ tipo: 'warning', msg: 'Sovrapposizione con un\'altra assenza esistente.' });

  // Fine rapporto
  if (config.dataFine && al > parseDate(config.dataFine)) {
    avvisi.push({ tipo: 'warning', msg: 'La richiesta si estende oltre la data di fine rapporto.' });
  }

  return { giorni, saldoDopo, avvisi };
}
